import { applyBrandColors } from '@@brand'
import { ApolloProvider } from '@apollo/client'
import { StrictMode } from 'react'
import { CookiesProvider } from 'react-cookie'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Route, Routes } from 'react-router'
import './i18n'

import { AboutPage } from '@pages/about'
import { BlogDetailPage, BlogPage } from '@pages/blog'
import { CartPage } from '@pages/cart'
import { CatalogPage, DynamicCatalogPage, NewProductsPage, PopularProductsPage, PreviouslyOrderedProductsPage, ProductGroupProductsPage, SaleProductsPage } from '@pages/catalog'
import { ContactsPage } from '@pages/contacts'
import { DeliveryPage } from '@pages/delivery'
import { LoyaltyProgramPage } from '@pages/loyalty-program'
import { MainPage } from '@pages/main'
import { NoMatchPage } from '@pages/no-match'
import { CheckoutPage } from '@pages/order'
import { PaymentPage } from '@pages/payment'
import { PaymentVerificationPage } from '@pages/payment-verification'
import { ProfilePage } from '@pages/personal/profile'
import { ProductDetailPage } from '@pages/product'
import { SearchPage } from '@pages/search'
import { ShopsPage } from '@pages/shops'
import { TestErrorPage } from '@pages/test-error'

import { personalRoutes } from '@widgets/personal/nav'


import { client } from '@shared/api/client'
import { 
  URL_BLOG,
  URL_BLOG_CATEGORY,
  URL_BLOG_POST,
  URL_BLOG_TAG,
  URL_BLOG_THEME,
  URL_CART,
  URL_CATEGORY_NEW_PRODUCTS,
  URL_CATEGORY_POPULAR_PRODUCTS,
  URL_CATEGORY_PREVIOUSLY_ORDERED,
  URL_CATEGORY_SALE_PRODUCTS,
  URL_CHECKOUT,
  URL_CUSTOMER_PROFILE,
  URL_LEGACY_CATEGORY,
  URL_LEGACY_CATEGORY_CHILDREN,
  URL_LEGACY_CATEGORY_SLUG,
  URL_LEGACY_CATEGORY_SUB_CHILDREN,
  URL_MAIN_PAGE,
  URL_PAGE_ABOUT,
  URL_PAGE_CATALOG,
  URL_PAGE_CONTACTS,
  URL_PAGE_DELIVERY_INFO,
  URL_PAGE_DYNAMIC_CATALOG,
  URL_PAGE_LOYALTY,
  URL_PAGE_PAYMENT_INFO,
  URL_PAGE_PRODUCT,
  URL_PAGE_PRODUCT_GROUP,
  URL_PAGE_SHOPS,
  URL_PAGE_TEST_SENTRY_ERROR,
  URL_SEARCH,
  URL_VERIFY_PAYMENT
} from '@shared/contants'
import { analytics, PageViewTracker } from '@shared/lib/analytics'
import { EmitterProvider } from '@shared/lib/emitter'
import { loadJivoSiteScript } from '@shared/lib/jivosite'
import { LocaleLayout, LocaleProvider, LocaleRedirect } from '@shared/lib/locale'
import { initSentry } from '@shared/lib/sentry'
import { bootstrapTheme } from '@shared/lib/theme/bootstrap'
import { CategoryRedirect } from '@shared/ui/category-redirect'
import { SentryErrorBoundary } from '@shared/ui/sentry-error-boundary'
import { RouteSEO } from '@shared/ui/seo/RouteSEO'
import '@shared/styles/app.scss'

import DeliveryPaymentLayout from './layouts/deliveryPayment'
import PersonalLayout from './layouts/personal/PersonalLayout'
import RootLayout from './layouts/RootLayout'

initSentry()

// =========================================================================
// Service Worker Kill Switch
// Заменяем старый Workbox SW на kill-switch для очистки кеша
// ВАЖНО: путь должен быть /service-worker.js - тот же что и у старого PWA!
// =========================================================================
const SW_CLEANUP_KEY = 'sw-cleanup-v2' // v2 - новая версия очистки
const isCleanupDone = localStorage.getItem(SW_CLEANUP_KEY)

if ('serviceWorker' in navigator) {
  // Слушаем сообщение от kill-switch SW
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'SW_KILLED') {
      console.log('[SW Kill] PWA killed, marking as done and reloading...')
      localStorage.setItem(SW_CLEANUP_KEY, 'true')
      window.location.reload()
    }
  })
  
  if (!isCleanupDone) {
    // Регистрируем kill-switch по тому же пути что и старый Workbox SW
    navigator.serviceWorker.register('/service-worker.js')
      .then((registration) => {
        console.log('[SW Kill] Kill-switch registered at /service-worker.js')
        
        // Принудительно проверяем обновления чтобы заменить старый SW
        registration.update().catch((err) => {
          console.log('[SW Kill] Update check failed:', err)
        })
        
        // Если есть waiting SW, принудительно активируем его
        if (registration.waiting) {
          console.log('[SW Kill] Found waiting SW, sending SKIP_WAITING')
          registration.waiting.postMessage({ type: 'SKIP_WAITING' })
        }
        
        // Слушаем появление нового SW
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && registration.waiting) {
                console.log('[SW Kill] New SW installed, sending SKIP_WAITING')
                registration.waiting.postMessage({ type: 'SKIP_WAITING' })
              }
            })
          }
        })
      })
      .catch((err) => {
        console.log('[SW Kill] Registration failed:', err)
        // Даже если регистрация не удалась, пробуем очистить кеши вручную
        caches.keys().then(names => {
          names.forEach(name => caches.delete(name))
          console.log('[SW Kill] Manually cleared caches:', names)
          localStorage.setItem(SW_CLEANUP_KEY, 'true')
        })
      })
  } 
  else {
    console.log('[SW Kill] Already cleaned up, ensuring no SW is registered')
    // Убеждаемся что SW не зарегистрирован
    navigator.serviceWorker.getRegistrations().then(registrations => {
      registrations.forEach(reg => {
        reg.unregister()
        console.log('[SW Kill] Unregistered leftover SW from scope:', reg.scope)
      })
    })
  }
}
// Инициализируем аналитику (GA4 + Яндекс.Метрика)
analytics.init()

// Инициализируем JivoSite
loadJivoSiteScript()

// Ранний bootstrap темы (синхронно применит сохраненные значения, затем обновит с бэка)
// Не ждем завершения, чтобы не блокировать рендер; сохраненные значения применяются мгновенно
void bootstrapTheme()

// Применяем цвета бренда из конфигурации
applyBrandColors()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <SentryErrorBoundary>
      <ApolloProvider client={client}>
        <CookiesProvider defaultSetOptions={{ path: URL_MAIN_PAGE }}>
          <EmitterProvider>
            <BrowserRouter>
              <LocaleProvider>
                <RouteSEO />
                <PageViewTracker />
                <Routes>
                  {/* Default home page without locale */}
                  <Route path="/" element={<RootLayout />}>
                    <Route index element={<MainPage />} />
                  </Route>

                  {/* Locale-prefixed routes */}
                  <Route path="/:locale" element={<LocaleLayout />}>
                    <Route element={<RootLayout />}>
                      <Route index element={<MainPage />} />
                  
                      {/* Legacy /category* --> /catalog */}
                      <Route path={URL_LEGACY_CATEGORY.slice(1)} element={<CategoryRedirect />} />
                      <Route path={URL_LEGACY_CATEGORY_SLUG.slice(1)} element={<CategoryRedirect />} />
                      <Route path={URL_LEGACY_CATEGORY_CHILDREN.slice(1)} element={<CategoryRedirect />} />
                      <Route path={URL_LEGACY_CATEGORY_SUB_CHILDREN.slice(1)} element={<CategoryRedirect />} />
                  
                      {/** Catalog pages */}
                      <Route 
                        path={URL_PAGE_CATALOG.slice(1)} 
                        element={<CatalogPage />} 
                      />
                  
                      {/* Динамический роут для бесконечной вложенности категорий */}
                      <Route
                        path={URL_PAGE_DYNAMIC_CATALOG.slice(1)}
                        element={<DynamicCatalogPage />}
                      />
                  
                      <Route path={URL_CATEGORY_SALE_PRODUCTS.slice(1)} element={<SaleProductsPage />} />
                      <Route path={URL_CATEGORY_POPULAR_PRODUCTS.slice(1)} element={<PopularProductsPage />} />
                      <Route path={URL_CATEGORY_PREVIOUSLY_ORDERED.slice(1)} element={<PreviouslyOrderedProductsPage />} />
                      <Route path={URL_CATEGORY_NEW_PRODUCTS.slice(1)} element={<NewProductsPage />} />

                      <Route path={URL_PAGE_PRODUCT_GROUP.slice(1)} element={<ProductGroupProductsPage />} />


                      {/** Product pages */}
                      <Route path={URL_PAGE_PRODUCT.slice(1)} element={<ProductDetailPage />} />


                      {/** Cart pages */}
                      <Route path={URL_CART.slice(1)} element={<CartPage />} />


                      {/** Profile pages */}
                      <Route path={URL_CUSTOMER_PROFILE.slice(1)} element={<PersonalLayout />}>
                        <Route index element={<ProfilePage />} />
                        {personalRoutes.map(({ path, element }) => (
                          <Route key={path} path={path.replace(URL_CUSTOMER_PROFILE + '/', '')} element={element} />
                        ))}
                      </Route>

                      {/** Checkout pages */}
                      <Route path={URL_CHECKOUT.slice(1)} element={<CheckoutPage />} />


                      {/** Payment verification page */}
                      <Route path={`${URL_VERIFY_PAYMENT.slice(1)}/:operationId`} element={<PaymentVerificationPage />} />


                      {/** Search pages */}
                      <Route path={URL_SEARCH.slice(1)} element={<SearchPage />} />

                  
                      {/** Static pages */}
                      <Route path={URL_PAGE_ABOUT.slice(1)} element={<AboutPage />} />
                      <Route path={URL_PAGE_CONTACTS.slice(1)} element={<ContactsPage />} />
                      <Route path={URL_PAGE_SHOPS.slice(1)} element={<ShopsPage />} />
                      {/* <Route path={URL_PAGE_POLICY} element={<PolicyPage />} /> */}
                      <Route
                        path={URL_PAGE_LOYALTY.slice(1)}
                        element={<LoyaltyProgramPage />}
                      />


                      <Route path={URL_BLOG.slice(1)} element={<BlogPage />} />
                  
                      <Route path={URL_BLOG_CATEGORY.slice(1)} element={<BlogPage />} />
                      <Route path={URL_BLOG_THEME.slice(1)} element={<BlogPage />} />
                      <Route path={URL_BLOG_TAG.slice(1)} element={<BlogPage />} />

                      <Route path={URL_BLOG_POST.slice(1)} element={<BlogDetailPage />} />
                      {/* временно скрыли Блог */}

                  
                      <Route element={<DeliveryPaymentLayout />}>
                        <Route path={URL_PAGE_PAYMENT_INFO.slice(1)} element={<PaymentPage />} />
                        <Route path={URL_PAGE_DELIVERY_INFO.slice(1)} element={<DeliveryPage />} />
                      </Route>

                      {/* Тестовая страница для проверки Sentry */}
                      { process.env.NODE_ENV === 'development' && <Route path={URL_PAGE_TEST_SENTRY_ERROR.slice(1)} element={<TestErrorPage />} /> }

                      <Route path="*" element={<NoMatchPage />} />
                    </Route>
                  </Route>

                  {/* Redirect non-localized URLs to default locale */}
                  <Route path="*" element={<LocaleRedirect />} />
                </Routes>
              </LocaleProvider>
            </BrowserRouter>
          </EmitterProvider>
        </CookiesProvider>
      </ApolloProvider>
    </SentryErrorBoundary>
  </StrictMode>,
)
